/**
 * Company diary statuses use the same labels and lozenge colours as Ops diary
 * tasks.
 *
 * One table rather than a copy per surface: the listing's status column, the
 * listing's status filter and the entry modal's status changer all render the same
 * four words without duplicating the design-token mapping.
 */

import { getStatusColours } from '@/utilities/useStatusColours';

export type CompanyDiaryStatus = 'TO_DO' | 'IN_PROGRESS' | 'PAUSED' | 'COMPLETED';

export const COMPANY_DIARY_STATUS_LABELS: Record<CompanyDiaryStatus, string> = {
    TO_DO: 'To do',
    IN_PROGRESS: 'In progress',
    PAUSED: 'Paused',
    COMPLETED: 'Completed',
};

/** The filter and dropdown option list, in lifecycle order. */
export const COMPANY_DIARY_STATUS_OPTIONS = (
    Object.entries(COMPANY_DIARY_STATUS_LABELS) as [CompanyDiaryStatus, string][]
).map(([value, label]) => ({ value, label }));

/**
 * An unrecognised status is labelled and coloured as itself rather than dropped:
 * a status the API grows before the CRM knows about it should show as a plain
 * lozenge, not as a blank cell.
 */
export const companyDiaryStatusLabel = (status: string | null | undefined): string =>
    COMPANY_DIARY_STATUS_LABELS[status as CompanyDiaryStatus] ?? status ?? '';

export const companyDiaryStatusClasses = (status: string | null | undefined): string =>
    getStatusColours(COMPANY_DIARY_STATUS_LABELS[status as CompanyDiaryStatus] ?? COMPANY_DIARY_STATUS_LABELS.TO_DO, {
        type: 'ACTION_STATUS',
    }).classes;

/**
 * What a status change says once it lands. Shared by the entry modal's header
 * changer and the list's status cell - the same write from two places, so the same
 * confirmation.
 */
export const COMPANY_DIARY_STATUS_TOASTS: Record<CompanyDiaryStatus, string> = {
    TO_DO: 'Diary entry moved to To do.',
    IN_PROGRESS: 'Diary entry moved to In progress.',
    PAUSED: 'Diary entry paused.',
    COMPLETED: 'Diary entry completed.',
};
